import {
  Component,
  Output,
  EventEmitter,
  Input,
  signal,
  OnDestroy,
  AfterContentInit,
  input,
  computed,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { PickerModule } from '@ctrl/ngx-emoji-mart';
import { Emoji } from '@ctrl/ngx-emoji-mart/ngx-emoji';
import { PopupRef } from '../../../services/popup/popup-ref';
import { Tab } from '../../u-tab-view/u-tab-view.component';
import { DisabledAction } from '../u-icon-picker/u-icon-picker.component';
import { UIconModule } from '../../u-icon/u-icon.module';
import { UEmojiModule } from '../../u-emoji/u-emoji.module';
import { UTabViewModule } from '../../u-tab-view/u-tab-view.module';
import { UFileUploadModule } from '../../u-file-upload/u-file-upload.module';
import { UButtonModule } from '../../u-button/u-button.module';
import { UiInfraModule } from '../../../ui-infra.module';
import { UThemeWrapperModule } from '../../u-theme-wrapper/u-theme-wrapper.module';

export const ICON_PICKER_TABS: Tab[] = [
  { id: 'emoji', name: 'Emoji' },
  { id: 'upload', name: 'Upload' },
];

@Component({
  selector: 'u-icon-wrapper',
  standalone: true,
  imports: [
    PickerModule,
    CommonModule,
    UIconModule,
    UEmojiModule,
    UTabViewModule,
    UFileUploadModule,
    UButtonModule,
    UiInfraModule,
    UThemeWrapperModule,
  ],
  templateUrl: './u-icon-picker-wrapper.component.html',
  styleUrls: ['./u-icon-picker-wrapper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UIconWrapperComponent implements OnDestroy, AfterContentInit {
  readonly acceptTypes = ['image/png', 'image/jpeg', 'image/svg+xml']; //support svg?

  @Output() clear = new EventEmitter<any>();
  @Output() cancel = new EventEmitter<any>();
  @Output() emojiSelect = new EventEmitter<any>();
  @Output() fileSelect = new EventEmitter<File>();
  @Output() fileError = new EventEmitter<string>();

  @Input() set: Emoji['set'] = 'apple';
  @Input() showPreview: boolean = true;
  @Input() emojiTooltip: boolean = false;
  @Input() autoFocus: boolean = false;
  @Input() color: string = '#ae65c5';
  @Input() emojiSize: Emoji['size'] = 24;
  @Input() darkMode: boolean = false;
  @Input() backgroundImageFn: Emoji['backgroundImageFn'];

  disabledActions = input<DisabledAction[]>([]);

  show = signal<boolean>(false);
  activeTab = signal<string>('emoji');
  isDirty = signal<boolean>(false);
  selectedFile = signal<File | null>(null);
  imagePreview = signal<string>('');

  TABS = computed(() => {
    const disabledActionsMap = new Map(this.disabledActions().map((action) => [action.id, action]));

    return ICON_PICKER_TABS.map((tab) => {
      const action = disabledActionsMap.get(tab.id);

      if (!action) {
        return {
          ...tab,
          disabled: false,
          tooltip: '',
        };
      }

      return {
        ...tab,
        disabled: true,
        tooltip: action.disabledReason,
      };
    });
  });

  constructor(private ref: PopupRef<UIconWrapperComponent, any>) {}

  ngAfterContentInit(): void {
    this.show.set(true);
  }

  ngOnDestroy(): void {
    const previewUrl = this.imagePreview();
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    if (this.selectedFile()) {
      this.selectedFile.set(null);
      this.imagePreview.set('');
    }
  }

  setActiveTab(tab: string): void {
    const selectedTab = this.TABS().find((t) => t.id === tab);
    if (selectedTab && !selectedTab.disabled) {
      this.activeTab.set(tab);
    }
  }

  onFileSelected(file: File | File[]): void {
    this.selectedFile.set(Array.isArray(file) ? file[0] : file);
    const urlFile = URL.createObjectURL(Array.isArray(file) ? file[0] : file);
    this.imagePreview.set(urlFile);
  }

  onFileError(event: string): void {
    this.selectedFile.set(null);
    this.fileError.emit(event);
  }

  saveImage() {
    this.fileSelect.emit(this.selectedFile());
    this.selectedFile.set(null);
    this.imagePreview.set('');
    this.ref.destroy();
  }

  cancelImage() {
    if (this.selectedFile()) {
      this.selectedFile.set(null);
      this.imagePreview.set('');
    } else {
      this.cancel.emit();
      this.ref.destroy();
    }
  }
}
